import { envelopeData } from '@/lib/envelope'
import { useState, useCallback, useEffect } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { resolveEntityQueryOptions, lessonDetailQueryOptions, lessonContextQueryOptions, lessonProgressQueryOptions, lessonResourcesQueryOptions } from '@/lib/queries/lesson'
import { VideoPlayer } from '@/components/watch/video-player'
import { LessonHeader } from '@/components/watch/lesson-header'
import { LessonInfoBar } from '@/components/watch/lesson-info-bar'
import { LessonNavigation } from '@/components/watch/lesson-navigation'
import { LessonResources } from '@/components/watch/lesson-resources'
import { LessonContent } from '@/components/watch/lesson-content'
import { InstructorSection } from '@/components/watch/instructor-section'
import { CommentsSection } from '@/components/watch/comments-section'
import { MarkCompleteButton } from '@/components/watch/mark-complete-button'
import { NextLessonCountdown } from '@/components/watch/next-lesson-countdown'
import { TimestampsSection } from '@/components/watch/timestamps-section'
import { NotFoundView } from '@/components/shared/not-found-view'
import { WatchPageSkeleton } from '@/components/shared/loading-skeleton'
import { useCourseLayout } from '@/components/watch/course-layout-context'
import { formatDuration } from '@/lib/utils'

export const Route = createFileRoute('/_app/$menu/$programa/$episodio')({
  loader: async ({ context, params }) => {
    let entity
    try {
      entity = envelopeData(
        await context.queryClient.fetchQuery(
          resolveEntityQueryOptions(params.menu, params.programa, params.episodio),
        ),
      ) ?? null
    } catch {
      return { entity: null }
    }


    if (entity?.hasAccess === false) {
      throw redirect({
        to: '/$menu/$programa',
        params: { menu: params.menu, programa: params.programa },
      })
    }

    if (entity?.entityId) {
      await Promise.allSettled([
        context.queryClient.prefetchQuery(lessonDetailQueryOptions(entity.entityId)),
        context.queryClient.prefetchQuery(lessonContextQueryOptions(entity.entityId)),
      ])
    }
    return { entity }
  },
  component: EpisodioPage,
  pendingComponent: () => <WatchPageSkeleton />,
  errorComponent: () => (
    <div className="text-center py-16">
      <p className="text-muted-foreground">Erro ao carregar episódio. Tente recarregar a página.</p>
    </div>
  ),
})

function EpisodioPage() {
  const { menu, programa: program, episodio } = Route.useParams()
  const [showCountdown, setShowCountdown] = useState(false)

  const { data: resolveData } = useSuspenseQuery(resolveEntityQueryOptions(menu, program, episodio))
  const entity = envelopeData(resolveData)

  if (!entity) return <NotFoundView />

  return <LessonView lessonId={entity.entityId} entity={entity} showCountdown={showCountdown} setShowCountdown={setShowCountdown} />
}

function LessonView({ lessonId, entity, showCountdown, setShowCountdown }: {
  lessonId: string
  entity: any
  showCountdown: boolean
  setShowCountdown: (v: boolean) => void
}) {
  const { setCurrentLessonId } = useCourseLayout()
  const { data: lessonData } = useQuery(lessonDetailQueryOptions(lessonId))
  const { data: contextData } = useQuery(lessonContextQueryOptions(lessonId))
  const { data: progressData } = useQuery(lessonProgressQueryOptions(lessonId))
  const { data: resourcesData } = useQuery(lessonResourcesQueryOptions(lessonId))

  const lesson = envelopeData(lessonData)
  const context = envelopeData(contextData)
  const progress = envelopeData(progressData)
  const resources = envelopeData(resourcesData) ?? []

  // Set current lesson in sidebar context
  useEffect(() => {
    setCurrentLessonId(lessonId)
    return () => setCurrentLessonId(undefined)
  }, [lessonId, setCurrentLessonId])

  const handleVideoEnded = useCallback(() => {
    if (context?.nextLesson) {
      setShowCountdown(true)
    }
  }, [context?.nextLesson, setShowCountdown])

  if (!lesson) return <WatchPageSkeleton />

  const menuSlug = entity.menuSlug
  const programSlug = entity.programSlug

  const prevLesson = context?.previousLesson
    ? { title: context.previousLesson.title, href: `/${menuSlug}/${programSlug}/${context.previousLesson.slug}` }
    : null

  const nextLesson = context?.nextLesson
    ? { title: context.nextLesson.title, href: `/${menuSlug}/${programSlug}/${context.nextLesson.slug}` }
    : null

  const chapters: Array<{ time: number; title: string }> = []

  return (
    <div className="space-y-4">
      <div className="relative">
        <VideoPlayer
          src={lesson.videoUrl || null}
          lessonId={lessonId}
          startAt={progress?.positionSeconds ?? 0}
          chapters={chapters.length > 0 ? chapters : undefined}
          onEnded={handleVideoEnded}
        />
        {showCountdown && nextLesson && (
          <NextLessonCountdown
            nextLesson={nextLesson}
            onCancel={() => setShowCountdown(false)}
          />
        )}
      </div>

      <LessonHeader
        lessonId={lessonId}
        title={lesson.title}
        courseTitle={context?.courseName}
        isSaved={lesson.isSaved ?? false}
      />

      <div className="flex items-center justify-between gap-4">
        <LessonInfoBar
          duration={formatDuration(lesson.duration)}
        />
        <MarkCompleteButton
          lessonId={lessonId}
          isCompleted={lesson.isCompleted ?? false}
        />
      </div>

      <LessonNavigation
        prevLesson={prevLesson}
        nextLesson={nextLesson}
      />

      {chapters.length > 0 && (
        <TimestampsSection
          timestamps={chapters}
          onSeek={() => {}}
        />
      )}

      {lesson.content && <LessonContent html={lesson.content} />}

      {resources.length > 0 && <LessonResources resources={resources} />}

      {lesson.authorName && (
        <InstructorSection
          name={lesson.authorName}
          bio={lesson.authorBio}
          avatarUrl={lesson.authorPhoto}
          jobTitle={lesson.authorJobTitle}
        />
      )}

      <CommentsSection entityId={lessonId} />
    </div>
  )
}
